// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// What a controller feels like (M5-03, M5-07).
//
// Four pulses, and that is the whole vocabulary. Haptics are one of the few things in a headset
// that a player cannot look away from, so the discipline is to have very few of them and to make
// each one mean exactly one thing:
//
// | Pulse | What it means |
// |---|---|
// | `press`   | a button went down — the third of XR-004's confirmations |
// | `release` | the same button came up, lighter, so a hold is distinguishable from a tap |
// | `plunger` | continuous while the plunger is drawn, ramping with tension (06 §2.2) |
// | `shove`   | the cabinet took a hit, scaled by how hard |
//
// **A pulse is fire-and-forget and must never be awaited.** `pulse()` returns a promise on every
// runtime and rejects on some — a controller that has gone to sleep, a session that ended between
// the decision and the call. Awaiting it inside a frame callback puts a controller's battery
// state on the critical path of drawing a frame, and an unhandled rejection ends up in the
// console once per flip.
//
// **The ramp is re-issued, not held.** WebXR has no "set intensity" call — only "pulse at this
// intensity for this long" — so a rising plunger tension is a short pulse re-issued every frame
// at the new intensity. Re-issuing every frame at 72 Hz is wasteful and audible as a buzz on some
// controllers, so it re-issues only when the intensity has moved enough to feel.

/** The slice of a WebXR haptic actuator this product uses. */
export interface HapticActuatorPort {
  pulse: (intensity: number, milliseconds: number) => Promise<boolean> | boolean
}

/** One pulse's shape. */
export interface PulseShape {
  readonly intensity: number
  readonly milliseconds: number
}

/**
 * A flipper button going down.
 *
 * Short and firm. A real flipper button is a microswitch with a hard click, and the haptic is
 * standing in for that click rather than for the solenoid — the solenoid is the sound.
 */
export const PRESS_PULSE: PulseShape = { intensity: 0.65, milliseconds: 18 }

/** The same button coming up. Deliberately about a third of the press. */
export const RELEASE_PULSE: PulseShape = { intensity: 0.22, milliseconds: 12 }

/** The cabinet taking a hit at full shove strength. Scaled down for softer ones. */
export const SHOVE_PULSE: PulseShape = { intensity: 0.9, milliseconds: 60 }

/**
 * Taking hold of the cabinet: a short firm thump, distinct from a button's click.
 *
 * Stronger and longer than `PRESS_PULSE`, because a grab is a commitment — the machine is about
 * to follow the hand — and a player needs to feel the difference between having taken hold and
 * having brushed past.
 */
export const GRAB_PULSE: PulseShape = { intensity: 0.8, milliseconds: 35 }

/** Putting it down again. Lighter, the way letting go is. */
export const DROP_PULSE: PulseShape = { intensity: 0.35, milliseconds: 20 }

/**
 * A snap rule catching the part in hand, on a controller (M13-13): twelve milliseconds, the
 * shortest thing a controller can say. A hand gets a click instead — hands have no haptics —
 * and both get the flash; the recommendation's sound-and-light twins.
 */
export const SNAP_PULSE: PulseShape = { intensity: 0.4, milliseconds: 12 }

/** How much the plunger's tension must move before the ramp is re-issued. */
const RAMP_STEP = 0.08

/** How long each re-issued ramp pulse lasts. Longer than a frame, so the ramp does not gap. */
const RAMP_MS = 40

/**
 * One controller's haptics.
 *
 * Holds the last ramp intensity so the plunger's continuous cue can be re-issued only when it
 * has changed enough to feel, and swallows every rejection.
 */
export class Haptics {
  private actuator: HapticActuatorPort | undefined
  private lastRamp = -1

  /** Point it at an actuator, or at nothing — hands have no haptics and that is not an error. */
  attach(actuator: HapticActuatorPort | undefined): void {
    if (actuator === this.actuator) return
    this.actuator = actuator
    this.lastRamp = -1
  }

  get available(): boolean {
    return this.actuator !== undefined
  }

  /** Fire a shaped pulse. Never throws, never blocks. */
  fire(shape: PulseShape, scale = 1): void {
    const intensity = clamp01(shape.intensity * scale)
    if (intensity <= 0) return
    this.send(intensity, shape.milliseconds)
  }

  /**
   * Hold a continuous cue at `intensity`, re-issuing only when it has moved enough to feel.
   *
   * Called every frame while the plunger is drawn. Calling it with the same value costs nothing.
   */
  ramp(intensity: number): void {
    const value = clamp01(intensity)
    if (value <= 0) {
      this.lastRamp = -1
      return
    }
    if (this.lastRamp >= 0 && Math.abs(value - this.lastRamp) < RAMP_STEP) return
    this.lastRamp = value
    this.send(value, RAMP_MS)
  }

  /** Stop holding a ramp. The pulse already issued expires on its own. */
  endRamp(): void {
    this.lastRamp = -1
  }

  private send(intensity: number, milliseconds: number): void {
    const actuator = this.actuator
    if (actuator === undefined) return
    try {
      const result = actuator.pulse(intensity, milliseconds)
      // Fire and forget. A controller that has gone to sleep rejects, and an unhandled rejection
      // would land in the console once per flip.
      if (typeof result === 'object' && result !== null && 'catch' in result) {
        void (result as Promise<boolean>).catch(() => {})
      }
    } catch {
      // Some runtimes throw synchronously once the session has ended. Silence is correct: a
      // missing buzz is not something to tell a player about.
    }
  }
}

function clamp01(v: number): number {
  if (!Number.isFinite(v) || v <= 0) return 0
  return v >= 1 ? 1 : v
}
